import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';

import {
  SnCitizenServiceGetVideosByGeographyParams,
  CreateIncidentPayload,
  UpdateIncidentPayload,
  SnCitizenServiceGetVideosByIncidentIdParams,
  CitizenMetadata,
} from '@lib/api';
import { SnCitizenVideoStream } from '@lib/api/entities';
import { FsCadHistoricalAdvancedMarker } from '@lib/cad';

import { SnIntegrationsAPI } from './integrations.service';

/** The number of videos to return per Citizen API call. */
const DEFAULT_PAGE_SIZE = 100;

export interface SnCitizenServicePaginatedResponse<T> {
  idx: number;
  uuid: string;
  results: T[];
  hasMore: boolean; // Whether there are more results to fetch.
}

export interface SnCitizenServicePaginatedOptions {
  dedupeBy?: { uuid: string; source: string };
}

@Injectable({ providedIn: 'root' })
export class SnCitizenService {
  @Output() public readonly viewTabMarkersCleared = new EventEmitter<void>();
  @Output() public readonly citizenViewTabFetchCompleted = new EventEmitter<
    SnCitizenVideoStream[]
  >();

  public viewTabIncidentMarkers: FsCadHistoricalAdvancedMarker[] = [];

  private _latestPaginatedUuid: { [source: string]: string } = {};

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _integrationsAPI: SnIntegrationsAPI,
  ) {}

  private _getCitizenBasePath(): string {
    const citizenIntegration = this._integrationsAPI.getIntegrationByCode<CitizenMetadata>('citizen');
    const isIncidentServiceEnabled = citizenIntegration?.meta?.citizenIncidentServiceEnabled === true;
    
    return isIncidentServiceEnabled 
      ? '/api/ims/proxy/citizen'
      : '/api/integrations/citizen/proxy';
  }

  /**
   * Queries Citizen's API for all videos that occurred within the provided incident.
   * @param params - {@link SnCitizenServiceGetVideosByIncidentIdParams}
   */
  private _getVideosByIncidentId(
    params: SnCitizenServiceGetVideosByIncidentIdParams,
  ): Observable<{ results: SnCitizenVideoStream[] }> {
    const { incidentId, from = 0, size = DEFAULT_PAGE_SIZE, sort = 0, enrich = true } = params;
    return this._httpClient
      .get(`${this._getCitizenBasePath()}/v1/incidents/${incidentId}/videos`, {
        params: { from, size, sort, enrich },
      })
      .pipe(
        map((response: any[]) => ({
          results: response.map((item: object) => new SnCitizenVideoStream(item)),
        })),
      );
  }

  /**
   * Queries Citizen's API for all videos that occurred within the provided incident.
   * @param params - {@link SnCitizenServiceGetVideosByIncidentIdParams} without from and size
   */
  public getAllVideosByIncidentId(
    params: Omit<SnCitizenServiceGetVideosByIncidentIdParams, 'from' | 'size'>,
  ): Observable<{ results: SnCitizenVideoStream[] }> {
    const { incidentId, sort = 0, enrich = true } = params;
    let from = 0;
    const size = 100;

    return this._getVideosByIncidentId({ incidentId, from, size, sort, enrich }).pipe(
      expand((response) => {
        if (response.results.length === size) {
          from += size;
          return this._getVideosByIncidentId({ incidentId, from, size, sort, enrich });
        }
        return EMPTY;
      }),
      reduce(
        (allResults, currentPage) => ({
          results: [...allResults.results, ...currentPage.results],
        }),
        { results: [] as SnCitizenVideoStream[] },
      ),
    );
  }

  /**
   * Queries Citizen's API for all videos that occurred within the provided polygon and within the provided timeframe.
   * Only videos that passed Citizen's content moderation filter will be included.
   * @param params - {@link SnCitizenServiceGetVideosByGeographyParams}
   */
  public getVideosByGeography(
    params: SnCitizenServiceGetVideosByGeographyParams,
  ): Observable<{ results: SnCitizenVideoStream[] }> {
    const {
      geometry,
      startDatetime,
      endDatetime,
      from = 0,
      size = DEFAULT_PAGE_SIZE,
      sort = 0,
      enrich = true,
    } = params;
    const body = {
      geometry,
      start_time: startDatetime?.toISOString(),
      end_time: endDatetime?.toISOString(),
      from,
      size,
      sort,
    };
    return this._httpClient
      .post(`${this._getCitizenBasePath()}/v1/video_query`, body, { params: { enrich } })
      .pipe(
        map((response: any[] | null) => ({
          results: (response ?? [])
            .map((item: object) => new SnCitizenVideoStream(item))
            .filter((result: SnCitizenVideoStream) => result.durationSeconds !== 9223372000), // There's something on the citizen side where any video with a duration of 9223372000 should be ignored, but the API still returns it as of 2/12/25.
        })),
      );
  }

  /**
   * Does the same as the above method, but groups the videos by incident.
   * @param params - {@link SnCitizenServiceGetVideosByGeographyParams}
   */
  public getVideosByGeographyPaginated(
    params: Omit<SnCitizenServiceGetVideosByGeographyParams, 'from' | 'size'>,
    paginationOptions: SnCitizenServicePaginatedOptions = {},
  ): Observable<SnCitizenServicePaginatedResponse<SnCitizenVideoStream>> {
    let source: string | undefined = undefined;
    let uuid: string | undefined = undefined;
    let returnIdx: number = 0;
    if (paginationOptions.dedupeBy) {
      uuid = paginationOptions.dedupeBy.uuid;
      source = paginationOptions.dedupeBy.source;
      this._latestPaginatedUuid[source] = uuid;
    }
    const { geometry, startDatetime, endDatetime, sort = 0, enrich = true } = params;
    const body = {
      geometry,
      start_time: startDatetime?.toISOString(),
      end_time: endDatetime?.toISOString(),
      from: 0,
      size: DEFAULT_PAGE_SIZE,
      sort,
    };
    return this._httpClient
      .post(`${this._getCitizenBasePath()}/v1/video_query`, body, { params: { enrich } })
      .pipe(
        expand((results: object[], idx: number) => {
          /** If The current UUID is not the latest one for the source, return an empty observable to stop paginating. */
          if (
            uuid &&
            source &&
            this._latestPaginatedUuid[source] &&
            this._latestPaginatedUuid[source] !== uuid
          )
            return EMPTY;
          const from = idx * 100 + 100;
          if (results.length > 0) {
            return this._httpClient.post(
              `${this._getCitizenBasePath()}/v1/video_query`,
              { ...body, from },
              { params: { enrich } },
            );
          } else return EMPTY;
        }),
        map((results: any[]) => ({
          idx: returnIdx++,
          uuid: uuid ?? Math.random().toString(36).substring(2, 15),
          hasMore: results.length > 0,
          results: results
            .filter((result: any) => result.duration_seconds !== 9223372000)
            .map((item: object) => new SnCitizenVideoStream(item)),
        })),
      );
  }

  /**
   * Step 1: POST request that returns an upload URL and presigned metadata to allow the upload of media information into a Citizen storage bucket.
   * An access URL will be returned that allows for the access of this media information and should be sent on a followup request to create the incident.
   * @param filename - The name of the file to upload
   * @returns - object with the following:
   * - accessUrl: An access URL that allows for the access of this media information and should be sent on a followup request to create the incident.
   * - uploadUrl: A URL that allows for the upload of media information into a Citizen storage bucket.
   * - fields: PresignedMetadata from Google that serves the signature for the presigned URL. They should be forwarded with the request to upload the file to GCS.
   */
  public postInitialMediaUpload(
    filename: string,
  ): Observable<{ accessUrl: string; uploadUrl: string; fields: any }> {
    return this._httpClient
      .post(`${this._getCitizenBasePath()}/v1/media_upload`, { filename })
      .pipe(
        map((response: any) => ({
          accessUrl: response.access_url,
          uploadUrl: response.upload_url,
          fields: response.fields,
        })),
      );
  }

  /**
   * Step 2: Upload file to Google Cloud Storage using the presigned URL and fields from step 1.
   * @param uploadUrl - The upload URL from postInitialMediaUpload response
   * @param fields - The presigned metadata fields from postInitialMediaUpload response
   * @param file - The file to upload
   * @returns - Observable that completes when upload is successful
   */
  public uploadFileToGCS(uploadUrl: string, fields: any, file: File): Observable<any> {
    const formData = new FormData();

    // Add all the required fields from the presigned response
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });

    // Add the actual file last
    formData.append('file', file);

    // Make the request directly to Google Cloud Storage
    return this._httpClient.post(uploadUrl, formData);
  }

  /**
   * Step 3: Create incident with Citizen using the access URL from step 1.
   * @param incidentData - Incident data including the media access URL. Either radius_miles or
   * GEOJSON geometries array (https://datatracker.ietf.org/doc/html/rfc7946#section-3.1) must be provided w/ Type 'polygon'.
   * If GEOJSON geometries array is provided, radius_miles must be null.
   * @returns - Observable with the created incident response
   */
  public createIncident(incidentData: CreateIncidentPayload): Observable<any> {
    return this._httpClient.post(`${this._getCitizenBasePath()}/v1/incidents`, incidentData);
  }

  /**
   * Update an existing Citizen incident. All fields are optional for updates.
   * A new notification is sent to anyone who received the original notification.
   * @param incidentId - The ID of the incident to update
   * @param updateData - Updated incident data. All fields are optional.
   * @returns - Observable with the update response
   */
  public updateIncident(incidentId: string, updateData: UpdateIncidentPayload): Observable<any> {
    return this._httpClient.post(
      `${this._getCitizenBasePath()}/v1/incidents/${incidentId}/updates`,
      updateData,
    );
  }

  public onCitizenViewTabFetchCompleted(videos: SnCitizenVideoStream[]): void {
    if (videos.length > 0) {
      this.citizenViewTabFetchCompleted.emit(videos);
    }
  }

  public clearMarkers(): void {
    this.viewTabIncidentMarkers.forEach((marker) => marker.remove());
    this.viewTabIncidentMarkers = [];
    this.viewTabMarkersCleared.emit();
  }
}
